/**
 * useGrtSupply — V2.K.4
 *
 * Fetches GRT supply data from CoinGecko :
 *  - circulating_supply : tokens currently in circulation
 *  - total_supply       : ≈ circulating (no big locked treasury)
 *  - max_supply         : null on CoinGecko (GRT has NO hard cap —
 *                         uncapped, governance-controlled inflation)
 *
 * The Graph is structurally similar to ICP in that it has no cap and
 * runs a mint/burn dynamic, but the levers are different :
 *   - Mint  : ~3 % annual issuance for indexer rewards (governance
 *             can dial down to 0 %).
 *   - Burn  : 1 % on query fees, 1 % on curation actions, 100 % of
 *             deposit taxes. After the Horizon upgrade (December
 *             2025) the 0.5 % delegation tax was removed.
 *   - Net   : ~+2 %/year currently, with a long-term trajectory
 *             toward neutral or slightly deflationary as new
 *             Horizon-era services (Token API, Tycho, Amp) add fee +
 *             burn flows.
 *
 * Refresh : every 5 minutes.
 */

import { useQuery } from '@tanstack/react-query';

export interface GrtSupplyData {
  circulating: number;
  total: number;
  /** Published reference value for annual issuance rate (governance
   *  set, ~3 % at writing per The Graph Academy + Coinbase research). */
  annualInflationPct: number;
  /** Published reference value for the average annual burn rate. */
  annualBurnPct: number;
  /** Net annual change in supply (inflation - burn). */
  netAnnualPct: number;
  /** Estimated annual mint (circulating × inflation). */
  annualMint: number;
  /** Estimated annual burn (circulating × burn rate). */
  annualBurn: number;
  lastUpdated: string | null;
}

// Reference values, last verified against thegraph.academy +
// Coinbase Institutional Tokenomics review + Messari State of The
// Graph Q4 2025. These act as orientation values shown in the UI.
const GRT_ANNUAL_INFLATION_PCT_REFERENCE = 3.0;
const GRT_ANNUAL_BURN_PCT_REFERENCE = 1.0;

async function fetchGrtSupply(): Promise<GrtSupplyData> {
  const response = await fetch(
    'https://api.coingecko.com/api/v3/coins/the-graph?localization=false&tickers=false&market_data=true&community_data=false&developer_data=false&sparkline=false',
  );
  if (!response.ok) {
    throw new Error(`CoinGecko returned ${response.status}`);
  }
  const json = await response.json();
  const md = json.market_data || {};
  const circulating = Number(md.circulating_supply) || 0;
  const total = Number(md.total_supply) || circulating;

  const annualMint = circulating * (GRT_ANNUAL_INFLATION_PCT_REFERENCE / 100);
  const annualBurn = circulating * (GRT_ANNUAL_BURN_PCT_REFERENCE / 100);

  return {
    circulating,
    total,
    annualInflationPct: GRT_ANNUAL_INFLATION_PCT_REFERENCE,
    annualBurnPct: GRT_ANNUAL_BURN_PCT_REFERENCE,
    netAnnualPct: GRT_ANNUAL_INFLATION_PCT_REFERENCE - GRT_ANNUAL_BURN_PCT_REFERENCE,
    annualMint,
    annualBurn,
    lastUpdated: json.last_updated || null,
  };
}

export function useGrtSupply() {
  return useQuery({
    queryKey: ['grt', 'supply'],
    queryFn: fetchGrtSupply,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });
}
